import React from "react";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import authApi from "../../service/IdApi";
import PageLoading from "../../components/PageLoading";
import PageLoadError from "../../components/PageLoadError";
import PageHeading from "../../components/PageHeading";
import { Button } from "primereact/button";
import { Mixpanel } from "../../utils/Mixpanel";
import { Dialog } from "primereact/dialog";
import { useAppContext } from "../../context/appContext";
import TokenService from "../../utils/TokenService";
import { throttle } from "lodash";
import LocalStorageService from "../../utils/LocalStorageService";

const isDevelopmentEnvironment = process.env.REACT_APP_ENVIRONMENT === "development";

const formatSubscriptionStatusLabel = (status) => {
    if (status === "paid") {
        return "Paid";
    }
    if (status === "cancelAtEnd") {
        return "Paid";
    }
    if (status === "trial") {
        return "Trial";
    }
    if (status === "canceled") {
        return "Canceled";
    }
    return "Unknown";
};

const formatCancelReasonLabel = (cancelReason) => {
    if (cancelReason === "trialExpired") {
        return "Trial expired";
    }
    if (cancelReason === "customerCanceled") {
        return "Customer canceled";
    }
    if (cancelReason === "paymentFailed") {
        return "Payment failed";
    }
    return "Unknown";
};

const getSubscriptionStatusStyles = (status) => {
    if (status === "paid") {
        return {
            box: "surface-0 border-1 border-green-200",
            header: "text-green-700",
            status: "text-green-800",
            accent: "bg-green-500",
        };
    }
    if (status === "cancelAtEnd") {
        return {
            box: "surface-0 border-1 border-orange-200",
            header: "text-orange-700",
            status: "text-orange-800",
            accent: "bg-orange-400",
        };
    }
    if (status === "trial") {
        return {
            box: "surface-0 border-1 border-blue-200",
            header: "text-blue-700",
            status: "text-blue-800",
            accent: "bg-blue-500",
        };
    }
    if (status === "canceled") {
        return {
            box: "surface-0 border-1 border-gray-300",
            header: "text-600",
            status: "text-700",
            accent: "bg-gray-400",
        };
    }
    return {
        box: "surface-50 border-1 border-gray-300",
        header: "text-600",
        status: "text-700",
        accent: "bg-gray-400",
    };
};

const SubscriptionStatusCard = ({ provider }) => {
    const status = provider?.subscriptionStatus;
    const styles = getSubscriptionStatusStyles(status);
    const statusLabel = formatSubscriptionStatusLabel(status);
    const showCancelling = status === "cancelAtEnd";
    const showCancelReason = status === "canceled";
    const cancelReasonLabel = showCancelReason ? formatCancelReasonLabel(provider?.cancelReason) : null;

    return (
        <div
            className={`flex-shrink-0 border-round-lg overflow-hidden shadow-1 ${styles.box}`}
            style={{ minWidth: "148px", maxWidth: "200px" }}
        >
            <div className={`h-1 ${styles.accent}`} />
            <div className="px-3 py-2 text-center">
                <div className={`text-xs font-semibold uppercase mb-1 ${styles.header}`} style={{ letterSpacing: "0.04em" }}>
                    Subscription
                </div>
                <div className={`text-xl font-bold line-height-2 ${styles.status}`}>
                    {statusLabel}
                </div>
                {showCancelling && (
                    <div className="text-xs text-orange-600 mt-1 font-medium">
                        Cancelling
                    </div>
                )}
                {showCancelReason && (
                    <div className="text-xs text-500 mt-1 line-height-3">
                        {cancelReasonLabel}
                    </div>
                )}
            </div>
        </div>
    );
};

const MyCompanies = () => {
    const { displayAlert } = useAppContext();
    const [providers, setProviders] = useState(null);
    const [providerCount, setProviderCount] = useState(0);
    const [providersLoaded, setProvidersLoaded] = useState(false);
    const [confirmCreateProviderDialog, setConfirmCreateProviderDialog] = useState(false);
    const [listeningForMessages, setListeningForMessages] = useState(false);
    const [loggedOutNetworkUi, setLoggedOutNetworkUi] = useState(false);
    const [loggedOutProviderUi, setLoggedOutProviderUi] = useState(false);
    const [loggedOutIdUi, setLoggedOutIdUi] = useState(false);
    const [switchingProvidersInProgress, setSwitchingProvidersInProgress] = useState(false);
    const [loggedInToNewProvider, setLoggedInToNewProvider] = useState(false);
    const [vanityName, setVanityName] = useState(null);

    const { isLoading, isError, data } = useQuery("Providers", async () => {
        const result = await authApi.get("auth/providers");
        setProviders(result.data.providers);
        setProviderCount(result.data.providers.length);
        setProvidersLoaded(true);
        return data;
    },
        { retry: 2, retryDelay: 3000 }
    );

    const switchCompanyApi = useMutation(async (companyId) => {
        try {
            const response = await authApi.post("auth/switch-provider", '{ "providerId": "' + companyId + '"}');
            return response.data;
        } catch (error) {
            throw error;
        }
    });

    const switchCompanyAction = async (companyId) => {
        await LocalStorageService.logoutProvider();
        setLoggedOutIdUi(true);
        switchCompanyApi.mutate(companyId, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                setLoggedInToNewProvider(true);
            },
        });
    };

    const confirmCreateProvider = () => {
        setConfirmCreateProviderDialog(true);
    }

    const openNew = () => {
        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/create-site";
    };

    const pageHeader = (
        <PageHeading pageTitle="My Companies">
            <div className="block">
            </div>
        </PageHeading>
    );

    useEffect(() => {
        Mixpanel.track("UI ID - My Companies page view");
    }, []);

    useEffect(() => {
        if (!switchingProvidersInProgress) {
            return;
        }

        const handleMessage = (event) => {
            if (event.data && event.data.type === "wallistNetworkUi:providerCacheRefreshComplete") {
                setLoggedOutNetworkUi(true);
            }
            if (event.data && event.data.type === "wallistProviderUi:providerLogoutComplete") {
                setLoggedOutProviderUi(true);
            }
        };

        window.addEventListener("message", handleMessage);
        setListeningForMessages(true);

        return () => {
            window.removeEventListener("message", handleMessage);
            setListeningForMessages(false);
        };
    }, [switchingProvidersInProgress]);

    useEffect(() => {
        if (switchingProvidersInProgress) {
            // We will wait for all caches to clear and the new provider to be logged in
            if (loggedOutNetworkUi && loggedOutProviderUi && loggedOutIdUi && loggedInToNewProvider) {
                if (TokenService.getProviderSubdomain()) {
                    if (vanityName) {
                        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
                    }
                    else {
                        window.location.href = TokenService.getProviderAppUrl();
                    }
                }
            }
        }
    }, [switchingProvidersInProgress, loggedOutNetworkUi, loggedOutProviderUi, loggedOutIdUi, loggedInToNewProvider]);

    useEffect(() => {
        if (loggedInToNewProvider) {
            // We will wait for a maximum of 5 seconds. If we can't clear the caches, then this is likely in development and one of the UI sites is not running
            setTimeout(() => {
                if (TokenService.getProviderSubdomain()) {
                    if (vanityName) {
                        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
                    }
                    else {
                        window.location.href = TokenService.getProviderAppUrl();
                    }
                }
            }, 5000);
        }
    }, [loggedInToNewProvider]);

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (!providersLoaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    const switchProviderClicked = throttle((e, id) => {
        e.preventDefault();
        setSwitchingProvidersInProgress(true);
        switchCompanyAction(id);
    }, 500, { trailing: false });

    const switchProviderToProfileClicked = throttle((id, vanityName) => {
        setSwitchingProvidersInProgress(true);
        setVanityName(vanityName);
        switchCompanyAction(id);
    }, 500, { trailing: false });

    const goToNetworkSite = () => {
        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/browse/companies";
    }

    const hideConfirmCreateProviderDialog = () => {
        setConfirmCreateProviderDialog(false);
    }

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                {providerCount > 0 && (
                    <div className="card pt-3 sm:pt-0">
                        <div className="text-lg mr-3 py-3">
                            Click on the company below to manage your business. If you own more than one company, you can <a href="#" onClick={confirmCreateProvider}>create another account</a> on Wallist to keep your businesses organized.
                        </div>
                        {providers.map((provider, index) =>
                            <div key={index} className=" px-2 py-3 border-bottom-1 border-400">
                                <div className={isDevelopmentEnvironment
                                    ? "flex flex-row align-items-center justify-content-between gap-3"
                                    : "flex flex-row"}>
                                    <div className={isDevelopmentEnvironment ? "flex flex-row flex-1 min-w-0" : "flex flex-row"}>
                                    <div className="hidden md:flex" style={{ width: "175px", height: "175px", backgroundColor: "#" + provider.profilePicBackgroundColor }} >
                                        {provider.profilePic && (
                                            <a href="#" onClick={(e) => switchProviderClicked(e, provider._id)} className="text-2xl pl-0 mb-2 pr-0 text-left">
                                                <img
                                                    src={provider.profilePic}
                                                    height={175}
                                                    width={175}
                                                    alt={provider.name}
                                                    style={{
                                                        objectFit: provider.profilePicFit,
                                                    }}
                                                />
                                            </a>
                                        )}
                                        {!provider.profilePic && (
                                            <div className="bg-gray-200 w-full h-full text-center">
                                                <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-a)', color: 'var(--surface-d)' }}></i>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex md:hidden flex-shrink-0" style={{ width: "75px", height: "75px", minWidth: "75px", backgroundColor: "#" + provider.profilePicBackgroundColor }}>
                                        {provider.profilePic && (
                                            <a href="#" onClick={(e) => switchProviderClicked(e, provider._id)} className="text-2xl pl-0 mb-2 pr-0 text-left">
                                                <img
                                                    src={provider.profilePic}
                                                    height={75}
                                                    width={75}
                                                    alt={provider.name}
                                                    style={{
                                                        objectFit: provider.profilePicFit,
                                                    }}
                                                />
                                            </a>
                                        )}
                                        {!provider.profilePic && (
                                            <div className="bg-gray-200 w-full h-full text-center">
                                                <i className="pi pi-image mt-3 p-2" style={{ fontSize: '2em', borderRadius: '50%', backgroundColor: 'var(--surface-a)', color: 'var(--surface-d)' }}></i>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-column justify-content-center ml-3 min-w-0">
                                        <a href="#" onClick={(e) => switchProviderClicked(e, provider._id)} className="text-2xl pl-0 mb-2 pr-0 text-left">{provider.name}</a>
                                        {provider.rating && (
                                            <div>
                                                {provider?.rating !== "0.0" && (
                                                    <div className="flex flex-row flex-wrap align-items-center">
                                                        <div className="flex">
                                                            <span className="mr-2">{provider.rating}</span>
                                                            <img src={process.env.REACT_APP_PUBLIC_SITE + "/assets/images/ratings/rating-" + provider.rating + ".png"} width={97} height={16} alt={provider.rating + " star rating with " + provider.reviewCount + " reviews"} title={provider.rating + " star rating with " + provider.reviewCount + " reviews"} />
                                                        </div>
                                                        <div className="flex ml-2">
                                                            {/* <a href={provider.uriPrefix + "/reviews"}>{provider.reviewCount} review{provider.reviewCount > 1 ? "s" : ""}</a> */}
                                                        </div>
                                                    </div>
                                                )}
                                                {(!provider.rating || provider.rating === "0.0") && (
                                                    <div className="flex flex-row align-items-center">
                                                        <div className="flex">
                                                            <img src={process.env.REACT_APP_PUBLIC_SITE + "/assets/images/ratings/rating-0.0.png"} width={97} height={16} alt="0 reviews" title="0 reviews" />
                                                        </div>
                                                        <div className="flex ml-2 pt-1">
                                                            <span>0 reviews</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="mt-2">
                                            {provider.address && (
                                                <span className="white-space-nowrap">
                                                    {provider.address}
                                                    {provider.address2 && <br />}
                                                    {provider.address2}
                                                </span>
                                            )}
                                            <span>
                                                {provider.address && <br />}
                                                {provider.city}
                                                {provider.city && <span>, </span>}
                                                {provider.stateCode} {provider.zipCode}
                                            </span>
                                        </div>
                                        {provider.vanityName && (
                                            <div className="mt-2" style={{ wordBreak: "break-all" }}>
                                                <a onClick={() => switchProviderToProfileClicked(provider._id, provider.vanityName)} className="cursor-pointer" >{"wallist.com/company/" + provider.vanityName}</a>
                                            </div>
                                        )}
                                    </div>
                                    </div>
                                    {isDevelopmentEnvironment && (
                                        <SubscriptionStatusCard provider={provider} />
                                    )}
                                </div>
                            </div>
                        )}
                        {switchingProvidersInProgress && (
                            <Dialog
                                visible={switchingProvidersInProgress}
                                style={{ width: '225px' }}
                                header="Switching Accounts"
                                modal
                                closable={false}
                            >
                                <div className="flex flex-column justify-content-center w-full" style={{ height: '75px' }}>
                                    <div className="text-lg text-center"><i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i></div>
                                    {listeningForMessages && (
                                        <>
                                            <iframe
                                                src={process.env.REACT_APP_NETWORK_SITE + "/refresh-provider-cache"}
                                                style={{ width: 1, height: 1, border: 'none' }}
                                            />
                                            <iframe
                                                src={process.env.REACT_APP_PROVIDER_SITE + "/logout"}
                                                style={{ width: 1, height: 1, border: 'none' }}
                                            />
                                        </>
                                    )}
                                </div>
                            </Dialog>
                        )}
                    </div>
                )}
                {providerCount === 0 && (
                    <div className="pages-panel card flex justify-content-center">
                        <div className="flex flex-column justify-content-center text-center" style={{ maxWidth: "1250px" }}>
                            <div className="text-2xl mb-2 font-semibold">You have no companies on Wallist</div>
                            <div className="text-lg mb-4">Where would you like to get started?</div>
                            <div className="grid">
                                <div className="col-12 md:col-6 p-5">
                                    <div className="font-bold text-xl mb-2">
                                        Are you looking for help?
                                    </div>
                                    <div className="mb-2">Find top-rated landscapers, painters, plumbers, electricians, and other professionals in your area to help you!</div>
                                    <div>
                                        <Button label="Find Service Providers" onClick={goToNetworkSite} icon="pi pi-search" />
                                    </div>
                                </div>
                                <div className="col-12 md:col-6 border-500 border-top-1 md:border-top-none border-left-0 md:border-left-1 p-5">
                                    <div className="font-bold text-xl mb-2">
                                        Are you a business owner?
                                    </div>
                                    <div className="mb-2">List your company profile on Wallist for free! You can also manage your customers, create quotes & invoices, and collect payments all through Wallist!</div>
                                    <div>
                                        <Button label="Create My Company Account" onClick={openNew} icon="pi pi-building" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Dialog
                visible={confirmCreateProviderDialog}
                style={{ width: '400px' }}
                breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                header="Confirm Account Creation"
                modal
                onHide={hideConfirmCreateProviderDialog}
                className="p-fluid"
            >
                <div className="mb-3 text-lg">
                    You already have a company on Wallist. Are you sure you want to create another service provider account on Wallist?
                </div>
                <div className="flex flex-row w-full justify-content-end">
                    <div className="mr-2 flex">
                        <Button label="Cancel" onClick={hideConfirmCreateProviderDialog} className="p-button-text p-button-danger mr-2" icon="pi pi-times" />
                        <Button label="Proceed" onClick={openNew} icon="pi pi-check" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default MyCompanies;
